
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  useTable,
  useFilters,
  usePagination,
} from 'react-table';
import moment from 'moment';
import swal from "sweetalert";
import {
  handOffState,
  payrollRunDescriptor,
  useSurfaceTaskDescriptor,
} from '../../../../context/TaskContextDescriptor';
import { useFeatures } from '../../../../context/FeatureContext';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { usePayrollFlow } from '../../../hooks/usePayrollFlow';

import SelectEmployeeModal from './SelectEmployeeModal';
import BankgirotFileDownloadModal from './BangirotFileDownloadModal';
import UndoStepModal from './UndoStepModal';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';

import {
  Calendar,
  CalendarDays,
  Users,
  CreditCard,
  Building2,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowLeft,
  Plus,
  Lock,
  Unlock,
  Download,
  Upload,
  Calculator,
  TrendingUp,
  Eye,
  ArrowRight,
  Info,
  XCircle,
  Send,
  Sparkles,
} from 'lucide-react';

import {
  Button,
  Dropdown,
  Spinner,
  Accordion,
  Alert,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";

// Note: STEPS constants are now provided by the usePayrollFlow hook as PAYROLL_STEPS

const formatCurrency = (amount) => {
  const numAmount = Number(amount) || 0;
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numAmount);
};

/**
 * Normalize payslip data from new backend format to expected frontend format
 * @param {Object} payslip - The payslip data from refactored backend
 * @param {Array} employees - Array of employee data to get bank account info
 */
const normalizePayslipData = (payslip, employees = []) => {
  // Find matching employee to get bank account information
  const employee = employees.find(emp => emp.id === payslip.employeeId);

  return {
    // Map new field names to what frontend components expect
    id: payslip.slipId,
    employeeId: payslip.employeeId,
    payrollId: payslip.payrollId,

    // Employee information from nested employee object
    name: payslip.employee.fullName,
    ssn: payslip.employee.ssn,

    // Financial fields - convert to numbers
    netpay: Number(payslip.netPay),
    grosspay: Number(payslip.grossPay),
    tax: Number(payslip.tax),
    socialseccontr: Number(payslip.socialSecContr),

    // Bank account information from employee data
    clearingnumber: employee?.bankAccount?.clearingNumber || employee?.clearingnumber || payslip.clearingnumber || '',
    accountnumber: employee?.bankAccount?.accountNumber || employee?.accountnumber || payslip.accountnumber || '',

    // Calculate total employee cost
    totalEmployeeCost: Number(payslip.grossPay) + Number(payslip.socialSecContr),

    // Keep additional fields for future use
    periodStartDate: payslip.periodStartDate,
    periodEndDate: payslip.periodEndDate,
    contractTaxTable: payslip.contractTaxTable,
    contractTaxColumn: payslip.contractTaxColumn,
    dateGenerated: payslip.dateGenerated,
    createdAt: payslip.createdAt,
    updatedAt: payslip.updatedAt,
  };
};


// Main component that brings everything together
const MonthlyPayrollPeriod = ({ commercialReadOnly = false }) => {
  const { companyId, year, month } = useParams();
  const history = useHistory();
  const { features } = useFeatures();
  // The contextual assistant entry point rides the same entitlement as the assistant page it
  // navigates to — offering a link to a route the tenant cannot open would be a dead end.
  const hasAiAssistant = Boolean(features?.ai_assistant);

  // Data states
  const [payrollData, setPayrollData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [rawPayslips, setRawPayslips] = useState([]); // Store raw payslip data for enrichment

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showAddPayslipModal, setShowAddPayslipModal] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoInfo, setUndoInfo] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);

  // Convenience accessor for current payroll
  const currentPayroll = payrollData?.[0] || null;

  // Task context (S4): tell the assistant which payroll run is on screen. Only the
  // run id crosses the wire; payroll authorization is the backend's, per reader.
  const taskDescriptor = useMemo(() => payrollRunDescriptor(currentPayroll), [currentPayroll]);
  useSurfaceTaskDescriptor(taskDescriptor);

  // Hand this payroll run to the assistant through ROUTER STATE, not the live store: navigating
  // away unmounts this page and clears the store before the chat route mounts. The assistant
  // re-validates what it receives, and the backend answers an unauthorized reader with an empty
  // capture regardless of what the descriptor claims.
  const handleAskAssistant = useCallback(() => {
    if (!taskDescriptor) return;
    history.push(`/${companyId}/ai-assistant`, handOffState(taskDescriptor));
  }, [companyId, history, taskDescriptor]);

  console.log('Current payroll:', currentPayroll);

  // Get month name for display
  const monthName = moment().month(parseInt(month) - 1).format('MMMM');

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Re-enrich payslips with employee data when employees are updated
  useEffect(() => {
    if (rawPayslips.length > 0 && employees.length > 0) {
      const enrichedPayslips = rawPayslips.map(slip => normalizePayslipData(slip, employees));

      console.log('Enriched payslips:', enrichedPayslips);

      setPayslips(enrichedPayslips);
    }
  }, [employees, rawPayslips]);

  // ------------------ Data Fetching ------------------

  const fetchPayroll = useCallback(async () => {
    try {
      const url = getApiUrl(`${companyId}/payroll/${year}/${month}`);
      const res = await axios.get(url, { withCredentials: true });

      console.log('Payroll data:', res.data);

      if (res.data.status === 'success') {
        console.log('Payroll data:', res.data.data);
        // Wrap single payroll object in array for consistency with rest of component
        const payrollArray = res.data.data ? [res.data.data] : [];

        console.log('Payroll array:', payrollArray);

        setPayrollData(payrollArray);
        return payrollArray;
      } else {
        return [];
      }
    } catch (err) {
      throw err;
    }
  }, [companyId, year, month]);

  const fetchEmployees = useCallback(async () => {
    try {
      const url = getApiUrl(`${companyId}/hr/employees?active=true`);
      const res = await axios.get(url, { withCredentials: true });

      setEmployees(res.data.data.activeEmployees);
      return res.data.data.activeEmployees;
    } catch (err) {
      throw err;
    }
  }, [companyId]);

  const fetchPayslips = useCallback(async (payrollId, employeesData = []) => {
    if (!payrollId) return [];

    try {
      const url = getApiUrl(`${companyId}/payroll/${payrollId}/payslips`);
      const res = await axios.get(url, { withCredentials: true });

      const rawData = Array.isArray(res.data) ? res.data : [];

      // Store raw data for potential re-enrichment
      setRawPayslips(rawData);

      // Normalize payslip data to match expected format, merging with employee data if available
      const normalizedPayslips = rawData.map(slip => normalizePayslipData(slip, employeesData));

      setPayslips(normalizedPayslips);
      return normalizedPayslips;
    } catch (err) {
      throw err;
    }
  }, [companyId]);

  const fetchVerifications = useCallback(async (payrollId) => {
    if (!payrollId) return [];

    try {
      const url = getApiUrl(`${companyId}/payroll/${payrollId}/verifications`);
      const res = await axios.get(url, { withCredentials: true });

      return res.data.data;
    } catch (err) {
      return [];
    }
  }, [companyId]);

  // Central function to refresh all data
  const refreshAllData = useCallback(async () => {
    try {
      const payrollDataResult = await fetchPayroll();
      const employeesData = await fetchEmployees();

      if (payrollDataResult && payrollDataResult.length > 0) {
        // Pass employees data to enrich payslips with bank account info
        await fetchPayslips(payrollDataResult[0].id, employeesData);
        const verifications = await fetchVerifications(payrollDataResult[0].id);

        console.log('Verifications:', verifications);

        // Always update payroll data with verification information
        const updatedPayrollData = payrollDataResult.map(payroll => ({
          ...payroll,
          verificationCreated: verifications ? verifications.hasVerification : false,
          verificationNumber: verifications && verifications.latestVerification ? verifications.latestVerification.number : null,
          verifications: verifications ? verifications.verifications || [] : [],
        }));

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setPayrollData(updatedPayrollData);
        }
      }
    } catch (err) {
      console.error('Error refreshing all data:', err);
      if (isMountedRef.current) {
        setError(err);
        toast.error('Det gick inte att uppdatera data');
      }
    }
  }, [fetchPayroll, fetchEmployees, fetchPayslips, fetchVerifications]);

  // ------------------ Hook Callbacks ------------------

  // Callback for step changes (used by the hook)
  const onStepChange = useCallback(async (targetStepId) => {
    if (!currentPayroll) return false;

    try {
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/step`);
      const response = await axios.put(url, { step: targetStepId }, { withCredentials: true });

      if (response.status === 200) {
        await refreshAllData();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating payroll step:', err);
      throw err;
    }
  }, [companyId, currentPayroll, refreshAllData]);

  // Callback for undo actions (used by the hook)
  const onUndoAction = useCallback((undoData) => {
    setUndoInfo({
      action: undoData.type,
      fromStep: undoData.fromStep,
      toStep: undoData.toStep,
      endpoint: undoData.endpoint,
      method: undoData.method,
      reason: '',
      setReason: (r) => setUndoInfo(prev => ({ ...prev, reason: r }))
    });
    setShowUndoModal(true);
  }, []);

  // ------------------ Payroll Flow Hook ------------------

  const {
    activeStep,
    stepOperationInProgress,
    currentStepConfig,
    currentStepIndex,
    canProceedToNext,
    canGoBack,
    getNextStep,
    getPreviousStep,
    isBackwardBlocked,
    getBackwardBlockMessage,
    canEnterStep,
    canExitCurrentStep,
    getExitMessage,
    navigateToStep,      // View-only navigation
    handleStepChange,    // Progress navigation with backend update
    goToNextStep,
    goToPreviousStep,
    setActiveStep,
    getStepProgress,
    STEPS: PAYROLL_STEPS,
    STEP_ORDER
  } = usePayrollFlow(currentPayroll, payslips, onStepChange, onUndoAction, companyId, refreshAllData);

  // Initial data load
  useEffect(() => {
    setLoading(true);
    refreshAllData().finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    });

    console.log("Payroll data loaded:", payrollData);

  }, [refreshAllData]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize active step based on payroll data (only on first load)
  // Don't auto-navigate when data refreshes - let user control their view
  const hasInitializedStep = useRef(false);

  useEffect(() => {
    if (currentPayroll && isMountedRef.current && !hasInitializedStep.current) {
      console.log('Current payroll data:', currentPayroll);

      // Backend should determine the correct step based on payroll state
      let determinedStep = currentPayroll.currentStep;

      // If backend doesn't have currentStep, determine it from payroll state
      if (!determinedStep) {
        if (!currentPayroll.isLocked) {
          determinedStep = PAYROLL_STEPS.MANAGE_PAYSLIPS.id;
        } else if (currentPayroll.isLocked && !currentPayroll.verificationCreated) {
          determinedStep = PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id;
        } else if (currentPayroll.verificationCreated && !currentPayroll.agiSubmitted) {
          determinedStep = PAYROLL_STEPS.AGI_DECLARATION.id;
        } else if (currentPayroll.agiSubmitted && !currentPayroll.taxesPaid) {
          determinedStep = PAYROLL_STEPS.PAY_TAXES.id;
        } else {
          determinedStep = PAYROLL_STEPS.OVERVIEW.id;
        }
      }

      if (isMountedRef.current) {
        setActiveStep(determinedStep);
        hasInitializedStep.current = true;
      }
    }
  }, [currentPayroll, PAYROLL_STEPS]);

  // ------------------ Event Handlers ------------------

  const handleStepClick = (stepKey) => {
    // When user clicks a step indicator, navigate to view it (no backend update)
    // This allows viewing any accessible step without losing progress
    navigateToStep(stepKey);
  };

  const handleAddPayslip = () => {
    if (currentPayroll?.isLocked) {
      toast.warning('Du måste låsa upp perioden för att lägga till lönespecifikationer');
    } else {
      setShowAddPayslipModal(true);
    }
  };

  const handleEmployeeSelect = async (selectedEmployees) => {
    if (!currentPayroll) return;

    try {
      const newPayslips = [];
      for (const empId of selectedEmployees) {
        const createPayslipApiUrl = getApiUrl(`${companyId}/payroll/payslip`);
        const response = await axios.post(
          createPayslipApiUrl,
          {
            employeeId: empId,
            payrollId: currentPayroll.id,
            payRollMonth: month,
            payRollYear: year,
          },
          { withCredentials: true }
        );

        if (response.data?.data) {
          newPayslips.push(response.data.data);
        }
      }

      // Refresh payslips after creation
      await fetchPayslips(currentPayroll.id);

      toast.success(`${selectedEmployees.length} lönebesked har skapats`);
    } catch (err) {
      console.error('Error creating payslips:', err);
      toast.error('Det gick inte att skapa lönebesked');
    } finally {
      setShowAddPayslipModal(false);
    }
  };

  //Handling step operations like locking, unlocking, and verifying payrolls







  // Handler for viewing verification details
  const handleViewVerification = useCallback(async (verificationId) => {
    try {
      // Note: stepOperationInProgress is now managed by the hook

      console.log('handleViewVerification called with:', verificationId);
      console.log('Type of verificationId:', typeof verificationId);

      // Extract the actual ID if verificationId is an object
      let actualVerificationId = verificationId;
      if (typeof verificationId === 'object' && verificationId !== null) {
        // Try common ID field names
        actualVerificationId = verificationId.id || verificationId.verification_id || verificationId.verificationId;
        console.log('Extracted verification ID from object:', actualVerificationId);
      }

      if (!actualVerificationId) {
        throw new Error('Invalid verification ID');
      }

      // Fetch full verification details
      const response = await axios.get(
        getApiUrl(`${companyId}/verification/${actualVerificationId}`),
        { withCredentials: true }
      );

      // Handle new nested format: verification object with transactions array
      if (response.data && response.data.verification_id && Array.isArray(response.data.transactions)) {
        const verificationData = response.data;
        const transactions = verificationData.transactions;

        const transformedVerification = {
          verification_id: verificationData.verification_id,
          date: verificationData.date,
          description: verificationData.description,
          series: verificationData.series,
          number: verificationData.number || 1,
          created_at: verificationData.created_at || new Date().toISOString(),
          transactions: transactions,
          debitSum: transactions.filter(t => t.transaction_type === 'debit').reduce((sum, t) => sum + parseFloat(t.amount), 0),
          creditSum: transactions.filter(t => t.transaction_type === 'credit').reduce((sum, t) => sum + parseFloat(t.amount), 0),
          // Add additional fields that VerificationDetailsModal expects
          reference: verificationData.reference || null,
          notes: verificationData.notes || null,
          created_by_info: verificationData.created_by_info || null,
          references: verificationData.references || null,
          invoice: verificationData.invoice || null
        };

        setSelectedVerification(transformedVerification);
        setShowVerificationDetails(true);
      } else {
        toast.error('Kunde inte hämta verifikatdetaljer');
      }
    } catch (error) {
      console.error('Error fetching verification details:', error);
      toast.error('Kunde inte hämta verifikatdetaljer');
    } finally {
      // Note: stepOperationInProgress is now managed by the hook
    }
  }, [companyId]);



  // ------------------ Rendering ------------------

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
        <p className="mt-3">Laddar lönedata...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <h5>Ett fel uppstod</h5>
        <p>{error.message || 'Kunde inte ladda lönedata'}</p>
        <Button onClick={refreshAllData}>Försök igen</Button>
      </Alert>
    );
  }

  if (!payrollData || !payrollData.length) {
    return (
      <Alert variant="warning">
        <h5>Ingen data</h5>
        <p>Ingen data för denna löneperiod.</p>
      </Alert>
    );
  }

  return (
    <div className="monthly-payroll-container">
      {/* Modern Header */}
      <fieldset disabled={commercialReadOnly} className="border-0 p-0 m-0"><ModernPayrollHeader
        year={year}
        month={month}
        currentPayroll={currentPayroll}
        payslips={payslips}
        onAddPayslip={handleAddPayslip}
        onAskAssistant={!commercialReadOnly && hasAiAssistant && taskDescriptor ? handleAskAssistant : undefined}
      /></fieldset>

      {/* Progress Tracker */}
      <PayrollProgressTracker
        currentPayroll={currentPayroll}
        activeStep={activeStep}
        onStepClick={handleStepClick}
        PAYROLL_STEPS={PAYROLL_STEPS}
      />

      {/* Step Content */}
      <fieldset disabled={commercialReadOnly} className="border-0 p-0 m-0"><PayrollStepContent
        currentPayroll={currentPayroll}
        activeStep={activeStep}
        payslips={payslips}
        employees={employees}
        onRefreshData={refreshAllData}
        onAddPayslip={handleAddPayslip}
        onRequestUndo={onUndoAction}
        onViewVerification={handleViewVerification}
        stepOperationInProgress={stepOperationInProgress}
        PAYROLL_STEPS={PAYROLL_STEPS}
      /></fieldset>

      {/* Step Navigation Bar */}
      {!commercialReadOnly && <StepNavigationBar
        currentPayroll={currentPayroll}
        payslips={payslips}
        activeStep={activeStep}
        onStepChange={handleStepChange}
        canProceedToNext={canProceedToNext}
        canGoBack={canGoBack}
        isBackwardBlocked={isBackwardBlocked}
        getBackwardBlockMessage={getBackwardBlockMessage}
        PAYROLL_STEPS={PAYROLL_STEPS}
        navigateToStep={navigateToStep}
        setActiveStep={setActiveStep}
        companyId={companyId}
        onRefreshData={refreshAllData}
      />}

      {/* Add Payslip Modal */}
      <SelectEmployeeModal
        show={showAddPayslipModal}
        handleClose={() => setShowAddPayslipModal(false)}
        handleSelect={handleEmployeeSelect}
        employees={employees}
        existingPayslips={payslips}
      />

      {/* Undo Step Modal */}
      <UndoStepModal
        show={showUndoModal}
        onClose={() => setShowUndoModal(false)}
        onConfirm={(reason) => {
          // Execute undo action using the hook's logic
          setShowUndoModal(false);

          // The actual undo logic is handled by the hook's onUndoAction callback
          // which sets up the undoInfo and shows the modal
          // Here we just need to handle the confirmation
          if (undoInfo) {
            // Execute the undo action
            const executeUndo = async () => {
              try {
                const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/${undoInfo.endpoint}`);

                // Build request data based on endpoint type
                let requestData;
                if (undoInfo.endpoint === 'verification') {
                  requestData = { method: 'annul', reason };
                } else if (undoInfo.endpoint === 'lock') {
                  // For lock undo, we need to send { isLocked: false }
                  requestData = undoInfo.data || { isLocked: false };
                } else {
                  requestData = { reason };
                }

                const response = await axios({
                  method: undoInfo.method,
                  url,
                  data: requestData,
                  withCredentials: true
                });

                // Handle response - lock endpoint doesn't return success field
                const isSuccess = response.data.success !== undefined
                  ? response.data.success
                  : response.status === 200;

                if (isSuccess) {
                  await refreshAllData();
                  toast.success(response.data.message || 'Åtgärden har ångrats');
                  setActiveStep(undoInfo.toStep);
                }
              } catch (err) {
                console.error('Error executing undo action:', err);
                toast.error(err.response?.data?.message || err.message || 'Ett oväntat fel uppstod');
              } finally {
                setUndoInfo(null);
              }
            };
            executeUndo();
          }
        }}
        undoInfo={undoInfo}
      />

      {/* Verification Details Modal */}
      <VerificationDetailsModal
        showVerificationDetails={showVerificationDetails}
        setShowVerificationDetails={setShowVerificationDetails}
        selectedRow={selectedVerification}
        onVerificationUpdated={refreshAllData}
        viewOnly={true}
      />
    </div>
  );
};

// Export the main component
export default MonthlyPayrollPeriod;


// Replace the existing ModernPayrollHeader component
const ModernPayrollHeader = ({ year, month, currentPayroll, payslips, onAddPayslip, onAskAssistant }) => {
  const monthName = moment().month(parseInt(month) - 1).format('MMMM');

  // Calculate totals from payslips for real-time updates (for unlocked payrolls)
  // Use cached values from payroll table for locked payrolls (for performance)
  const calculateTotalsFromPayslips = () => {
    if (!payslips || payslips.length === 0) {
      return {
        totalGrossSalary: 0,
        totalNetSalary: 0,
        totalTax: 0,
        totalSocialSecurity: 0,
        totalTaxesAndFees: 0,
        numPayslips: 0
      };
    }

    const totalGrossSalary = payslips.reduce((sum, slip) => sum + parseFloat(slip.grosspay || 0), 0);
    const totalNetSalary = payslips.reduce((sum, slip) => sum + parseFloat(slip.netpay || 0), 0);
    const totalTax = payslips.reduce((sum, slip) => sum + parseFloat(slip.tax || 0), 0);
    const totalSocialSecurity = payslips.reduce((sum, slip) => sum + parseFloat(slip.socialseccontr || 0), 0);

    return {
      totalGrossSalary,
      totalNetSalary,
      totalTax,
      totalSocialSecurity,
      totalTaxesAndFees: totalTax + totalSocialSecurity,
      numPayslips: payslips.length
    };
  };

  // Use real-time calculations for unlocked payrolls, cached values for locked ones
  const useRealTimeCalculations = !currentPayroll?.isLocked;
  const calculatedTotals = calculateTotalsFromPayslips();

  const displayTotals = useRealTimeCalculations ? calculatedTotals : {
    totalGrossSalary: parseFloat(currentPayroll?.totalAmountPaid || 0),
    totalNetSalary: parseFloat(currentPayroll?.totalAmountPaid || 0) - parseFloat(currentPayroll?.totalTaxAmount || 0),
    totalTax: parseFloat(currentPayroll?.totalTaxAmount || 0),
    totalSocialSecurity: parseFloat(currentPayroll?.socialSecurityPaid || 0),
    totalTaxesAndFees: parseFloat(currentPayroll?.totalTaxAmount || 0) + parseFloat(currentPayroll?.socialSecurityPaid || 0),
    numPayslips: currentPayroll?.numPayslips || 0
  };

  return (
    <>
      <style>{`
        /* Semantic color variables for payroll steps */
        :root {
          --step-bg: #f8fafc;
          --step-bg-hover: #f1f5f9;
          --step-bg-muted: #f3f4f6;
          --step-bg-subtle: #fafafa;
          --step-border: #e2e8f0;
          --step-border-light: #e5e7eb;
          --step-border-hover: #cbd5e1;
          --step-border-disabled: #d1d5db;
          --step-text-heading: #1e293b;
          --step-text-secondary: #475569;
          --step-text-label: #374151;
          --step-text-muted: #6b7280;
          --step-text-disabled: #9ca3af;

          --step-success-bg: #f0fdf4;
          --step-success-bg-light: #dcfce7;
          --step-success-border: #bbf7d0;
          --step-success-border-dark: #a7f3d0;
          --step-success-border-light: #d1fae5;
          --step-success-border-accent: #86efac;
          --step-success-text: #166534;
          --step-success-text-dark: #14532d;
          --step-success-text-medium: #15803d;
          --step-success-text-deep: #065f46;
          --step-success-accent: #059669;

          --step-warning-bg: #fef3c7;
          --step-warning-border: #fde68a;
          --step-warning-text: #b45309;
          --step-warning-text-dark: #92400e;

          --step-alert-bg: #fffbeb;
          --step-alert-border: #fed7aa;
          --step-alert-icon: #ea580c;
          --step-alert-text: #9a3412;

          --step-danger-bg: #fef2f2;
          --step-danger-bg-light: #fee2e2;
          --step-danger-border: #fecaca;
          --step-danger-border-dark: #fca5a5;
          --step-danger-border-hover: #f87171;
          --step-danger-text: #dc2626;
        }

        .payroll-header {
          background: var(--step-bg);
          border: 1px solid var(--step-border);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          color: var(--step-text-heading);
        }

        .payroll-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--step-text-heading);
        }
        
        .payroll-subtitle {
          font-size: 0.875rem;
          color: var(--bs-gray);
          margin-bottom: 1rem;
        }
        
        .payroll-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .stat-card {
          background: white;
          border: 1px solid var(--step-border);
          border-radius: 6px;
          padding: 0.75rem;
        }
        
        .stat-label {
          font-size: 0.75rem;
          color: var(--bs-gray);
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          font-weight: 500;
          letter-spacing: 0.5px;
        }
        
        .stat-value {
          font-size: 1rem;
          font-weight: 600;
          color: var(--step-text-heading);
        }
        
        .header-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .header-btn {
          background: var(--bs-primary);
          border: 1px solid var(--bs-primary);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
        }

        .header-btn:hover:not(:disabled) {
          background: var(--primary-hover);
          border-color: var(--primary-hover);
          color: white;
          transform: translateY(-1px);
        }
        
        .header-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        
        .status-indicator {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.75rem;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .status-locked {
          background: var(--step-danger-bg);
          color: var(--step-danger-text);
          border: 1px solid var(--step-danger-border);
        }

        .status-open {
          background: var(--step-success-bg);
          color: var(--step-success-text);
          border: 1px solid var(--step-success-border);
        }

        .status-info {
          background: var(--step-success-bg);
          color: var(--step-success-text);
          border: 1px solid var(--step-success-border);
        }
        
        .header-meta {
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
          .payroll-header {
            padding: 1rem;
          }
          
          .payroll-title {
            font-size: 1.25rem;
          }
          
          .payroll-stats {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 0.75rem;
          }
          
          .header-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
        }
      `}</style>

      <div className="payroll-header">
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1 }}>
            <h1 className="payroll-title">
              <Calendar size={24} color="var(--bs-primary)" />
              Lönekörning {monthName} {year}
            </h1>
            <p className="payroll-subtitle">
              Hantera löneprocessen för {displayTotals.numPayslips} anställda
            </p>

            <div className="header-meta">
              <div className={`status-indicator ${currentPayroll?.isLocked ? 'status-locked' : 'status-open'}`}>
                {currentPayroll?.isLocked ? (
                  <>
                    <Lock size={14} />
                    Låst period
                  </>
                ) : (
                  <>
                    <Unlock size={14} />
                    Öppen period
                  </>
                )}
              </div>

              <div className="status-indicator status-info">
                <Clock size={14} />
                Utbetalning: {currentPayroll?.payoutDate ?
                  moment(currentPayroll.payoutDate).format('YYYY-MM-DD') :
                  'Ej satt'
                }
              </div>
            </div>
          </div>

          <div className="header-actions">
            {onAskAssistant && (
              <button className="header-btn" onClick={onAskAssistant}>
                <Sparkles size={16} />
                Fråga assistenten
              </button>
            )}
            <button
              className="header-btn"
              onClick={onAddPayslip}
              disabled={currentPayroll?.isLocked}
            >
              <Plus size={16} />
              Lägg till lönespec
            </button>
          </div>
        </div>

        {/* Compact Stats - REAL-TIME CALCULATIONS */}
        <div className="payroll-stats">
          <div className="stat-card">
            <div className="stat-label">Antal lönespecar</div>
            <div className="stat-value">
              {displayTotals.numPayslips}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Total bruttolön</div>
            <div className="stat-value">
              {formatCurrency(displayTotals.totalGrossSalary)}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Total nettolön</div>
            <div className="stat-value">
              {formatCurrency(displayTotals.totalNetSalary)}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Skatter & avgifter</div>
            <div className="stat-value">
              {formatCurrency(displayTotals.totalTaxesAndFees)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// Add this Progress Tracker component after the ModernPayrollHeader
const PayrollProgressTracker = ({ currentPayroll, activeStep, onStepClick, PAYROLL_STEPS }) => {
  const steps = [
    {
      id: 1,
      title: 'Lönespecifikationer',
      description: 'Skapa och hantera lönebesked',
      icon: Users,
      key: PAYROLL_STEPS.MANAGE_PAYSLIPS.id
    },
    {
      id: 2,
      title: 'Betala & Bokför',
      description: 'Utbetalning och bokföring',
      icon: CreditCard,
      key: PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id
    },
    {
      id: 3,
      title: 'AGI Deklaration',
      description: 'Skapa och skicka AGI-fil',
      icon: FileText,
      key: PAYROLL_STEPS.AGI_DECLARATION.id
    },
    {
      id: 4,
      title: 'Betala Skatter & Avgifter',
      description: 'Inbetalning till Skatteverket',
      icon: Calculator,
      key: PAYROLL_STEPS.PAY_TAXES.id
    },
    {
      id: 5,
      title: 'Bokför dragningar',
      description: 'Bokför Skatteverkets dragningar',
      icon: Building2,
      key: PAYROLL_STEPS.BOOK_WITHDRAWAL.id
    },
    {
      id: 6,
      title: 'Översikt',
      description: 'Sammanfattning och rapporter',
      icon: TrendingUp,
      key: PAYROLL_STEPS.OVERVIEW.id
    }
  ];

  const getCurrentStep = () => {
    if (!currentPayroll) return 1;
    const currentStepKey = currentPayroll.currentStep || PAYROLL_STEPS.MANAGE_PAYSLIPS.id;
    const stepMap = {
      [PAYROLL_STEPS.MANAGE_PAYSLIPS.id]: 1,
      [PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id]: 2,
      [PAYROLL_STEPS.AGI_DECLARATION.id]: 3,
      [PAYROLL_STEPS.PAY_TAXES.id]: 4,
      [PAYROLL_STEPS.BOOK_WITHDRAWAL.id]: 5,
      [PAYROLL_STEPS.OVERVIEW.id]: 6
    };
    return stepMap[currentStepKey] || 1;
  };

  const isStepCompleted = (stepId) => {
    return getCurrentStep() > stepId;
  };

  const isStepActive = (stepId) => {
    return getCurrentStep() === stepId;
  };

  const isStepViewing = (stepKey) => {
    return activeStep === stepKey;
  };

  const isStepAccessible = (stepId) => {
    const currentStep = getCurrentStep();
    return stepId <= currentStep || isStepCompleted(stepId);
  };

  const getStatus = (step) => {
    if (isStepCompleted(step.id)) return 'completed';
    if (isStepActive(step.id)) return 'current';
    return 'pending';
  };

  return (
    <>
      <style>{`
  .pl-progress-tracker {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--step-border-light);
  }

  .pl-step-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-weight: 600;
    font-size: 0.875rem;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .pl-step-indicator.completed {
    background: var(--bs-success);
    color: white;
  }

  .pl-step-indicator.current {
    background: var(--bs-primary);
    color: white;
    animation: pl-pulse 2s infinite;
  }

  .pl-step-indicator.pending {
    background: var(--step-bg-muted);
    color: var(--step-text-muted);
  }

  .pl-step-indicator.viewing {
    outline: 3px solid var(--bs-primary);
    outline-offset: 2px;
  }


  @keyframes pl-pulse {
    0% {
      box-shadow: 0 0 0 0 var(--rgba-primary-4);
    }
    70% {
      box-shadow: 0 0 0 10px transparent;
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }

  .pl-step-block {
    cursor: default;
  }

  .pl-step-block.accessible {
    cursor: pointer;
  }

  .pl-step-block.accessible:hover .pl-step-label {
    color: var(--bs-primary);
  }

  .pl-step-label {
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--step-text-heading);
    transition: color 0.2s ease;
  }

  .pl-step-sublabel {
    font-size: 0.75rem;
    color: var(--step-text-muted);
  }

  .pl-step-viewing-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.65rem;
    color: var(--bs-primary);
    font-weight: 600;
    margin-top: 2px;
  }

  .pl-step-block.completed .pl-step-label {
    color: var(--step-success-text-deep);
  }

  .pl-step-block.current .pl-step-label {
    color: var(--bs-primary);
  }

  .pl-step-block.pending .pl-step-label {
    color: var(--step-text-disabled);
  }

  .pl-step-block.pending .pl-step-sublabel {
    color: var(--step-border-disabled);
  }

  @media (max-width: 768px) {
    .pl-steps-row {
      flex-direction: column !important;
      gap: 0.5rem;
    }

    .pl-step-block {
      background: var(--step-bg);
      border-radius: 8px;
      border: 1px solid var(--step-border-light);
      padding: 0.75rem 1rem;
    }
  }

  /* Verification Info Card Styles */
  .verification-info-card {
    background: var(--step-success-bg);
    border: 1px solid var(--bs-primary);
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .verification-header {
    border-bottom: 1px solid var(--step-success-border-dark);
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  .verification-details {
    background: white;
    border-radius: 6px;
    padding: 1rem;
    border: 1px solid var(--step-success-border-light);
  }

  .verification-item {
    padding: 1rem;
    border: 1px solid var(--step-success-border-light);
    border-radius: 6px;
    background: var(--step-bg-subtle);
    margin-bottom: 0.75rem;
  }

  .verification-item:last-child {
    margin-bottom: 0;
  }

  .verification-field {
    margin-bottom: 0.5rem;
  }

  .verification-field label {
    font-weight: 600;
    color: var(--step-text-label);
    font-size: 0.875rem;
    display: block;
    margin-bottom: 0.25rem;
  }

  .verification-field span {
    color: var(--step-text-muted);
    font-size: 0.875rem;
  }

  .verification-actions {
    border-top: 1px solid var(--step-border-light);
    padding-top: 0.75rem;
  }
`}</style>

      <div className="pl-progress-tracker">
        <div className="d-flex align-items-center justify-content-between pl-steps-row">
          {steps.map((step, index) => {
            const status = getStatus(step);
            const viewing = isStepViewing(step.key);
            const accessible = isStepAccessible(step.id);
            const Icon = step.icon;

            return (
              <React.Fragment key={step.id}>
                <div
                  className={`d-flex align-items-center pl-step-block ${status} ${accessible ? 'accessible' : ''}`}
                  onClick={() => {
                    if (accessible && onStepClick) {
                      onStepClick(step.key);
                    }
                  }}
                >
                  <div className={`pl-step-indicator ${status} ${viewing ? 'viewing' : ''}`}>
                    {status === 'completed' ? (
                      <CheckCircle2 size={20} />
                    ) : (
                      <Icon size={18} />
                    )}
                  </div>
                  <div className="ms-3">
                    <div className="pl-step-label">{step.title}</div>
                    <div className="pl-step-sublabel">{step.description}</div>
                    {viewing && status !== 'current' && (
                      <div className="pl-step-viewing-badge">
                        <Eye size={11} />
                        Visar
                      </div>
                    )}
                  </div>
                </div>

              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};


// Add this Step Content Component after the PayrollProgressTracker
const PayrollStepContent = ({ currentPayroll, activeStep, payslips, employees, onRefreshData, onAddPayslip, onRequestUndo, onViewVerification, stepOperationInProgress, PAYROLL_STEPS }) => {
  const { companyId } = useParams();
  const [operationInProgress, setOperationInProgress] = useState(false);

  const verificationInfo = useMemo(() => {
    const allVerifications = currentPayroll?.verifications || [];

    console.log('All verifications:', allVerifications);

    // Filter to only show active, non-annulment verifications for UI display
    const activeVerifications = allVerifications.filter(v =>
      v.status === 'ACTIVE' && v.reference_number !== 'ANNULMENT'
    );

    return {
      hasVerification: currentPayroll?.verificationCreated || false,
      verificationNumber: currentPayroll?.verificationNumber || null,
      verifications: activeVerifications, // Only show active verifications in UI
      allVerifications: allVerifications // Keep all verifications for audit/debugging
    };
  }, [currentPayroll?.verificationCreated, currentPayroll?.verificationNumber, currentPayroll?.verifications]);

  const renderStepContent = () => {
    // Calculate if user is viewing their current progress step
    const currentProgressStep = currentPayroll?.currentStep || PAYROLL_STEPS.MANAGE_PAYSLIPS.id;
    const isViewingCurrentProgress = activeStep === currentProgressStep;

    switch (activeStep) {
      case PAYROLL_STEPS.MANAGE_PAYSLIPS.id:
        return <PayslipManagementStep
          currentPayroll={currentPayroll}
          payslips={payslips}
          employees={employees}
          onRefreshData={onRefreshData}
          operationInProgress={operationInProgress}
          setOperationInProgress={setOperationInProgress}
          onAddPayslip={onAddPayslip}
          PAYROLL_STEPS={PAYROLL_STEPS}
          activeStep={activeStep}
          isViewingCurrentProgress={isViewingCurrentProgress}
        />;

      case PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id:
        return <PaymentBookkeepingStep
          currentPayroll={currentPayroll}
          payslips={payslips}
          verificationInfo={verificationInfo}
          onRefreshData={onRefreshData}
          operationInProgress={operationInProgress}
          setOperationInProgress={setOperationInProgress}
          onRequestUndo={onRequestUndo}
          onViewVerification={onViewVerification}
          stepOperationInProgress={stepOperationInProgress}
          isViewingCurrentProgress={isViewingCurrentProgress}
        />;

      case PAYROLL_STEPS.AGI_DECLARATION.id:
        return <AGIDeclarationStep
          currentPayroll={currentPayroll}
          payslips={payslips}
          operationInProgress={operationInProgress}
          setOperationInProgress={setOperationInProgress}
          onRefreshData={onRefreshData}
          isViewingCurrentProgress={isViewingCurrentProgress}
        />;

      case PAYROLL_STEPS.PAY_TAXES.id:
        return <TaxPaymentStep
          currentPayroll={currentPayroll}
          operationInProgress={operationInProgress}
          setOperationInProgress={setOperationInProgress}
          onRefreshData={onRefreshData}
          isViewingCurrentProgress={isViewingCurrentProgress}
        />;

      case PAYROLL_STEPS.BOOK_WITHDRAWAL.id:
        return <BookWithdrawalStep
          currentPayroll={currentPayroll}
          payslips={payslips}
          companyId={companyId}
          onRefreshData={onRefreshData}
          operationInProgress={operationInProgress}
          setOperationInProgress={setOperationInProgress}
          onViewVerification={onViewVerification}
          isViewingCurrentProgress={isViewingCurrentProgress}
        />;

      case PAYROLL_STEPS.OVERVIEW.id:
        return <OverviewStep
          currentPayroll={currentPayroll}
          payslips={payslips}
        />;

      default:
        return <PayslipManagementStep
          currentPayroll={currentPayroll}
          payslips={payslips}
          employees={employees}
          onRefreshData={onRefreshData}
          operationInProgress={operationInProgress}
          setOperationInProgress={setOperationInProgress}
          onAddPayslip={onAddPayslip}
          PAYROLL_STEPS={PAYROLL_STEPS}
        />;
    }
  };

  return (
    <>
      <style>{`
        .step-content-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--step-border-light);
          overflow: hidden;
          margin-bottom: 2rem;
        }

        .step-header {
          background: linear-gradient(135deg, var(--step-bg) 0%, var(--step-bg-hover) 100%);
          padding: 2rem;
          border-bottom: 1px solid var(--step-border-light);
        }
        
        .step-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .step-title h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--step-text-heading);
          margin: 0;
        }
        
        .step-description {
          color: var(--bs-gray);
          font-size: 0.875rem;
          line-height: 1.5;
          margin: 0;
        }
        
        .step-body {
          padding: 2rem;
        }
        
        .info-card {
          background: var(--step-success-bg);
          border: 1px solid var(--step-success-border-dark);
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 0.75rem;
        }

        .info-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .info-text {
          font-size: 0.875rem;
          color: var(--step-success-text-dark);
          line-height: 1.5;
          margin: 0;
        }
        
        .warning-card {
          background: var(--step-alert-bg);
          border: 1px solid var(--step-alert-border);
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 0.75rem;
        }

        .warning-icon {
          color: var(--step-alert-icon);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .warning-text {
          font-size: 0.875rem;
          color: var(--step-alert-text);
          line-height: 1.5;
          margin: 0;
        }
        
        .action-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .action-card {
          background: var(--step-bg);
          border: 1px solid var(--step-border);
          border-radius: 8px;
          padding: 1.5rem;
          transition: all 0.2s ease;
        }

        .action-card:hover {
          background: var(--step-bg-hover);
          border-color: var(--step-border-hover);
          transform: translateY(-2px);
        }
        
        .action-card-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        
        .action-card-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--bs-primary), var(--primary-hover));
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        
        .action-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--step-text-heading);
          margin: 0;
        }
        
        .action-card-description {
          font-size: 0.875rem;
          color: var(--bs-gray);
          line-height: 1.4;
          margin-bottom: 1rem;
        }
        
        .action-button {
          width: 100%;
          border-radius: 6px;
          padding: 0.75rem 1rem;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-primary-action {
          background: var(--bs-primary);
          color: white;
        }

        .btn-primary-action:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }
        
        .btn-secondary-action {
          background: var(--step-bg-hover);
          color: var(--bs-gray);
          border: 1px solid var(--step-border);
        }

        .btn-secondary-action:hover:not(:disabled) {
          background: var(--step-border);
          color: var(--step-text-secondary);
        }
        
        .btn-success-action {
          background: var(--bs-primary);
          color: white;
        }

        .btn-success-action:hover:not(:disabled) {
          background: var(--primary-hover);
        }

        .btn-danger-outline {
          background: white;
          color: var(--step-danger-text);
          border: 1px solid var(--step-danger-border);
        }

        .btn-danger-outline:hover:not(:disabled) {
          background: var(--step-danger-bg);
          border-color: var(--step-danger-border-hover);
        }
        
        .btn-danger-action {
          background: var(--step-danger-bg-light);
          color: var(--step-danger-text);
          border: 1px solid var(--step-danger-border-dark);
        }

        .btn-danger-action:hover:not(:disabled) {
          background: var(--step-danger-border);
          border-color: var(--step-danger-border-hover);
        }
        
        .action-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        
        .verification-status-inline {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--step-success-bg);
          border: 1px solid var(--step-success-border-accent);
          border-radius: 6px;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          color: var(--step-success-text);
        }
        
        .verification-link {
          color: var(--bs-primary);
          text-decoration: underline;
          cursor: pointer;
          font-weight: 600;
        }

        .verification-link:hover {
          color: var(--primary-hover);
        }
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }
        
        .status-success {
          background: var(--step-success-bg-light);
          color: var(--step-success-text);
          border: 1px solid var(--step-success-border);
        }

        .status-warning {
          background: var(--step-warning-bg);
          color: var(--step-warning-text-dark);
          border: 1px solid var(--step-warning-border);
        }

        .status-info {
          background: var(--step-success-bg-light);
          color: var(--step-success-text);
          border: 1px solid var(--step-success-border);
        }
        
        .step-navigation {
          background: var(--step-bg);
          padding: 1.5rem 2rem;
          border-top: 1px solid var(--step-border-light);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .nav-button {
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-nav-secondary {
          background: var(--step-bg-hover);
          color: var(--bs-gray);
          border: 1px solid var(--step-border);
        }

        .btn-nav-secondary:hover:not(:disabled) {
          background: var(--step-border);
          color: var(--step-text-secondary);
        }
        
        .btn-nav-primary {
          background: var(--bs-primary);
          color: white;
        }

        .btn-nav-primary:hover:not(:disabled) {
          background: var(--primary-hover);
        }
        
        .nav-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
          .step-header,
          .step-body {
            padding: 1.5rem;
          }
          
          .action-grid {
            grid-template-columns: 1fr;
          }
          
          .step-navigation {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .nav-button {
            justify-content: center;
          }
        }
      `}</style>

      <div className="step-content-container">
        {renderStepContent()}
      </div>
    </>
  );
};

const PayslipManagementStep = ({
  currentPayroll,
  payslips,
  employees,
  onRefreshData,
  operationInProgress,
  setOperationInProgress,
  onAddPayslip,
  PAYROLL_STEPS,
  activeStep,
  isViewingCurrentProgress
}) => {
  const { companyId } = useParams();

  const handleLockToggle = async () => {
    if (!currentPayroll) return;

    setOperationInProgress(true);
    try {
      const isLocking = !currentPayroll.isLocked;
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/lock`);
      await axios.put(url, { isLocked: isLocking }, { withCredentials: true });

      // NO step advancement - lock/unlock is independent of progression
      // User will use the "Fortsätt" button to advance when ready

      await onRefreshData();

      if (isLocking) {
        toast.success('Perioden är nu låst. Du kan granska lönespecifikationerna och sedan klicka på "Fortsätt" för att gå vidare.');
      } else {
        toast.success('Du kan nu redigera lönespecifikationer igen.');
      }
    } catch (error) {
      toast.error('Det gick inte att ändra låsstatus');
    } finally {
      setOperationInProgress(false);
    }
  };

  // Determine if unlock is allowed
  const canUnlock =
    isViewingCurrentProgress &&  // Must be on current progress step, not just viewing
    !currentPayroll?.verificationCreated &&  // No verification created yet
    !currentPayroll?.agiSubmitted &&  // AGI not submitted yet
    !currentPayroll?.taxesPaid;  // Taxes not paid yet

  // Delete payslip handler
  const handleDeletePayslip = async (payslipId) => {
    setOperationInProgress(true);
    try {
      const url = getApiUrl(`${companyId}/payslip/${payslipId}`);
      await axios.delete(url, { withCredentials: true });

      // Refresh payslips after delete
      await onRefreshData();

      toast.success('Lönebeskedet har raderats');
    } catch (err) {
      console.error('Error deleting payslip:', err);
      toast.error('Det gick inte att radera lönebeskedet');
    } finally {
      setOperationInProgress(false);
    }
  };

  return (
    <>
      <div className="step-header">
        <div className="step-title">
          <Users size={24} color="var(--bs-primary)" />
          <h3>Hantera lönespecifikationer</h3>
        </div>
        <p className="step-description">
          Skapa och hantera lönebesked för dina anställda. När alla lönespecifikationer är klara,
          lås perioden för att fortsätta till nästa steg.
        </p>
      </div>

      <div className="step-body">
        {/* Zero payslips info banner */}
        {payslips?.length === 0 && !currentPayroll?.isLocked && (
          <div className="info-card" style={{ marginTop: '1rem' }}>
            <Info size={20} className="info-icon" style={{ color: 'var(--bs-primary)' }} />
            <div>
              <div className="info-text">
                <strong>Ingen lön denna månad?</strong><br />
                Om du inte har några löner att betala ut denna månad kan du låsa perioden direkt.
                Du måste fortfarande skicka AGI-deklaration till Skatteverket även om inga löner betalas ut.
              </div>
            </div>
          </div>
        )}

        <div className="action-grid">
          <div className="action-card">
            <div className="action-card-header">
              <div className="action-card-icon">
                <Plus size={20} />
              </div>
              <h4 className="action-card-title">Lägg till lönespecifikationer</h4>
            </div>
            <p className="action-card-description">
              Välj anställda och skapa lönebesked för denna period. Du kan lägga till flera anställda samtidigt.
            </p>
            <button
              className="action-button btn-primary-action"
              onClick={onAddPayslip}
              disabled={operationInProgress || currentPayroll?.isLocked}
            >
              <Plus size={16} />
              Lägg till lönespec
            </button>
          </div>

          <div className="action-card">
            <div className="action-card-header">
              <div className="action-card-icon">
                {currentPayroll?.isLocked ? <Unlock size={20} /> : <Lock size={20} />}
              </div>
              <h4 className="action-card-title">
                {currentPayroll?.isLocked ? 'Lås upp period' : 'Lås period'}
              </h4>
            </div>
            <p className="action-card-description">
              {currentPayroll?.isLocked ?
                'Lås upp perioden för att kunna redigera lönespecifikationer igen.' :
                'Lås perioden för att fortsätta. Du kan låsa perioden även utan lönespecifikationer om ingen lön ska betalas ut denna månad.'
              }
            </p>
            <button
              className={`action-button ${currentPayroll?.isLocked ? 'btn-secondary-action' : 'btn-primary-action'}`}
              onClick={handleLockToggle}
              disabled={operationInProgress || (currentPayroll?.isLocked && !canUnlock)}
            >
              {operationInProgress ? (
                <div className="loading-spinner" />
              ) : currentPayroll?.isLocked ? (
                <Unlock size={16} />
              ) : (
                <Lock size={16} />
              )}
              {currentPayroll?.isLocked ? 'Lås upp' : 'Lås period'}
            </button>

            {/* Show single most relevant blocker message when unlock is disabled */}
            {!!currentPayroll?.isLocked && !canUnlock && (() => {
              const message = !isViewingCurrentProgress
                ? 'Du kan bara låsa upp från ditt nuvarande steg'
                : currentPayroll?.taxesPaid
                  ? 'Kan inte låsas upp - skatter är redan betalda'
                  : currentPayroll?.agiSubmitted
                    ? 'Kan inte låsas upp - AGI är skickat till Skatteverket'
                    : currentPayroll?.verificationCreated
                      ? 'Kan inte låsas upp - bokföringsverifikat är skapat'
                      : null;
              if (!message) return null;
              return (
                <small className="text-muted mt-2 d-block" style={{ fontSize: '0.75rem', color: 'var(--step-danger-text)' }}>
                  <AlertCircle size={12} style={{ marginBottom: '2px' }} /> {message}
                </small>
              );
            })()}
          </div>
        </div>

        {/* Payslips Table - NOW ACTUALLY RENDERED */}
        <div className="mt-4">
          <h5 className="mb-3">Lönespecifikationer ({payslips?.length || 0})</h5>
          <PayslipsTable
            payslips={payslips}
            companyId={companyId}
            onDeletePayslip={handleDeletePayslip}
            isLocked={currentPayroll?.isLocked}
          />
        </div>
      </div>
    </>
  );
};


const PaymentBookkeepingStep = ({ currentPayroll, payslips, verificationInfo, onRefreshData, operationInProgress, setOperationInProgress, onRequestUndo, onViewVerification, stepOperationInProgress, isViewingCurrentProgress }) => {
  const { companyId } = useParams();
  const [showBGModal, setShowBGModal] = useState(false);
  const isPayDateInFuture = currentPayroll?.payoutDate && moment(currentPayroll.payoutDate).isAfter(moment(), 'day');

  const handleBGEmployeeSalariesSelect = (selectedEmployeeSalaries, customerNumber, bankgirotNumber) => {
    setShowBGModal(false);
    downloadBankGirotFile(customerNumber, bankgirotNumber, selectedEmployeeSalaries);
  };

  function convertDateFormat(dateString) {
    const date = new Date(dateString);
    const y = date.getFullYear().toString().substr(-2);
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return y + m + d;
  }

  const downloadBankGirotFile = async (customerNumber, bankgirotNumber, payments) => {
    if (!currentPayroll) return;

    setOperationInProgress(true);
    try {
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/bankgirot-file`);
      const resp = await axios.post(
        url,
        {
          product: 'LÖN',
          customerNumber,
          bankGiroNumber: bankgirotNumber,
          paymentDate: convertDateFormat(currentPayroll.payoutDate),
          payments: payments.map((p) => ({
            amount: p.netpay,
            reference: 'LÖN',
            accountNumber: p.accountnumber,
            clearingNumber: p.clearingnumber,
            name: p.name,
            ssn: p.ssn.replace('-', ''),
            message: `LÖN ${currentPayroll.year}${currentPayroll.month}`,
            senderorgnr: '5592139090',
          })),
        },
        {
          responseType: 'text',
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );

      const text = resp.data;
      const blob = new Blob([text], { type: 'text/plain; charset=latin1' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'bankgirot.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Bankgirot-filen har skapats och laddats ner');
    } catch (err) {
      console.error('Error downloading Bankgirot file:', err);
      toast.error('Det gick inte att skapa bankgirofilen');
    } finally {
      setOperationInProgress(false);
    }
  };

  const generateBookKeepingEntry = async () => {
    if (!currentPayroll) return;

    setOperationInProgress(true);
    try {
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/verification`);
      const resp = await axios.post(url, { withCredentials: true });

      console.log('Bookkeeping entry response:', resp.data);

      if (resp.status === 201) {
        await onRefreshData();
        toast.success('Verifikat har skapats för löneutbetalningen');
      }
    } catch (err) {
      console.error('Error generating bookkeeping entry:', err);
      toast.error('Det gick inte att skapa bokföringsverifikat');
    } finally {
      setOperationInProgress(false);
    }
  };

  return (
    <>
      <div className="step-header">
        <div className="step-title">
          <CreditCard size={24} color="var(--bs-primary)" />
          <h3>Betala och bokför löner</h3>
        </div>
        <p className="step-description">
          Betala ut löner till dina anställda och skapa bokföringsverifikat för att registrera betalningen i bokföringen.
        </p>
      </div>

      <div className="step-body">
        {/* Zero payslips info banner for Step 2 */}
        {currentPayroll?.isLocked && payslips?.length === 0 && (
          <div className="info-card" style={{ marginBottom: '1.5rem' }}>
            <Info size={20} className="info-icon" style={{ color: 'var(--bs-primary)' }} />
            <div>
              <div className="info-text">
                <strong>Inga löner att betala ut</strong><br />
                Det finns inga lönespecifikationer för denna period. Du kan hoppa över utbetalning
                och bokföring och gå direkt till AGI-deklarationen genom att klicka på "Fortsätt".
              </div>
            </div>
          </div>
        )}

        <div className="action-grid">
          <div className="action-card">
            <div className="action-card-header">
              <div className="action-card-icon">
                <Download size={20} />
              </div>
              <h4 className="action-card-title">Ladda ner Bankgirofil</h4>
            </div>
            <p className="action-card-description">
              Skapa en bankgirofil för automatisk utbetalning av löner via din bank.
              Detta kräver att du har bankgirotjänst.
            </p>
            <button
              className="action-button btn-primary-action"
              onClick={() => setShowBGModal(true)}
              disabled={!currentPayroll?.isLocked || operationInProgress || !isViewingCurrentProgress}
            >
              <Download size={16} />
              Skapa BG-fil
            </button>
          </div>

          <div className="action-card">
            <div className="action-card-header">
              <div className="action-card-icon">
                <Building2 size={20} />
              </div>
              <h4 className="action-card-title">Bokföringsverifikat</h4>
            </div>

            {/* Inline verification status */}
            {verificationInfo?.hasVerification && (
              <div className="verification-status-inline">
                <CheckCircle2 size={16} className="text-success" />
                <span>
                  Verifikat{' '}
                  {verificationInfo.verifications && verificationInfo.verifications.length > 0 && (
                    <span
                      className="verification-link"
                      onClick={() => {
                        const verificationData = verificationInfo.verifications[0];
                        const verificationId = verificationData.id || verificationData.verification_id || verificationData.verificationId;
                        onViewVerification(verificationId);
                      }}
                      title="Klicka för att visa verifikatdetaljer"
                    >
                      V{verificationInfo.verifications[0].number}
                    </span>
                  )}
                  {' '}skapat
                </span>
              </div>
            )}

            <p className="action-card-description">
              {verificationInfo?.hasVerification
                ? 'Verifikatet är registrerat i bokföringen.'
                : 'Registrera löner, skatter och avgifter i bokföringen.'
              }
            </p>

            <button
              className={verificationInfo?.hasVerification ? 'action-button btn-danger-action' : 'action-button btn-primary-action'}
              onClick={verificationInfo?.hasVerification
                ? async () => {
                  // Undo verification
                  const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Ångra verifikat?',
                    text: 'Detta kommer att radera bokföringsverifikatet. Åtgärden kan inte ångras.',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, ångra',
                    cancelButtonText: 'Avbryt',
                    confirmButtonColor: '#dc2626'
                  });

                  if (result.isConfirmed) {
                    setOperationInProgress(true);
                    try {
                      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/verification`);
                      const response = await axios.delete(url, {
                        data: { method: 'annul', reason: 'Användaren ångrade verifikatet för att göra ändringar' },
                        withCredentials: true
                      });

                      if (response.data.success) {
                        await onRefreshData();

                        // Backend resets step to Step 1, but we want to stay on Step 2
                        // Update the step back to Step 2
                        try {
                          const stepUrl = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/step`);
                          await axios.put(stepUrl, {
                            step: 'step_2_pay_and_bookkeeping',
                            direction: 'forward'
                          }, { withCredentials: true });
                          await onRefreshData();
                        } catch (stepError) {
                          console.error('Error updating step:', stepError);
                        }

                        toast.success('Verifikatet har ångrats');
                      }
                    } catch (err) {
                      console.error('Error undoing verification:', err);
                      toast.error(err.response?.data?.message || 'Det gick inte att ångra verifikatet');
                    } finally {
                      setOperationInProgress(false);
                    }
                  }
                }
                : generateBookKeepingEntry
              }
              disabled={
                !currentPayroll?.isLocked ||
                payslips?.length === 0 ||
                (verificationInfo?.hasVerification && currentPayroll?.agiSubmitted) ||
                (!verificationInfo?.hasVerification && isPayDateInFuture) ||
                operationInProgress ||
                !isViewingCurrentProgress
              }
            >
              {operationInProgress ? (
                <div className="loading-spinner" />
              ) : verificationInfo?.hasVerification ? (
                <>
                  <XCircle size={16} />
                  Ångra verifikat
                </>
              ) : (
                <>
                  <FileText size={16} />
                  Skapa verifikat
                </>
              )}
            </button>

            {/* Disabled message when AGI submitted */}
            {verificationInfo?.hasVerification && currentPayroll?.agiSubmitted && (
              <small className="text-muted mt-2 d-block" style={{ fontSize: '0.75rem' }}>
                <AlertCircle size={12} style={{ marginBottom: '2px' }} /> Kan inte ångras - AGI är skickat. Du måste ångra AGI-deklarationen först.
              </small>
            )}

            {/* Disabled message when pay date is in the future */}
            {!verificationInfo?.hasVerification && isPayDateInFuture && (
              <small style={{ fontSize: '0.75rem', color: 'var(--step-warning-text)', display: 'block', marginTop: '0.5rem' }}>
                <AlertCircle size={12} style={{ marginBottom: '2px' }} /> Utbetalningsdatum {moment(currentPayroll.payoutDate).format('YYYY-MM-DD')} har inte passerat ännu
              </small>
            )}
          </div>
        </div>

        {/* Manual Payment Table */}
        {payslips && payslips.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h6 style={{ marginBottom: '1rem' }}>Manuell utbetalning via internetbank</h6>
            <p style={{ fontSize: '0.875rem', color: 'var(--bs-gray)', marginBottom: '1rem' }}>
              Om du vill betala ut lönerna manuellt, använd informationen nedan för att skapa betalningar i din internetbank.
            </p>
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead style={{ backgroundColor: 'var(--step-bg)' }}>
                  <tr>
                    <th>Anställd</th>
                    <th>Clearing</th>
                    <th>Kontonummer</th>
                    <th>Nettolön</th>
                  </tr>
                </thead>
                <tbody>
                  {payslips.map((pslip) => (
                    <tr key={pslip.id}>
                      <td style={{ fontWeight: '500' }}>{pslip.name}</td>
                      <td>{pslip.clearingnumber}</td>
                      <td>{pslip.accountnumber}</td>
                      <td style={{ fontWeight: '600' }}>{formatCurrency(pslip.netpay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Bankgiro Modal */}
      <BankgirotFileDownloadModal
        show={showBGModal}
        handleClose={() => setShowBGModal(false)}
        handleSelect={handleBGEmployeeSalariesSelect}
        employeeSalaries={payslips || []}
      />
    </>
  );
};

const AGIDeclarationStep = ({ currentPayroll, payslips, operationInProgress, setOperationInProgress, onRefreshData, isViewingCurrentProgress }) => {
  const { companyId } = useParams();

  const getAGIXML = async () => {
    if (!currentPayroll) return;

    setOperationInProgress(true);
    try {
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/agixml`);
      const resp = await axios.get(url, {
        responseType: 'blob',
        withCredentials: true
      });

      const xmlContent = await resp.data.text();
      const fileName = `agi_${currentPayroll.month}_${currentPayroll.year}.xml`;
      const blob = new Blob([xmlContent], { type: 'application/xml' });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();

      toast.success('AGI-filen har laddats ner och kan skickas till Skatteverket');
    } catch (err) {
      console.error('Error downloading AGI file:', err);
      // When responseType is 'blob', error response is also a blob - need to parse it
      let errorMessage = 'Det gick inte att skapa AGI-filen';
      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (parseErr) {
          // If parsing fails, use default message
        }
      } else if (err.response?.data?.message || err.response?.data?.error) {
        errorMessage = err.response.data.message || err.response.data.error;
      }

      // Show more prominent dialog for missing company settings
      if (errorMessage.includes('Settings') || errorMessage.includes('contact')) {
        Swal.fire({
          icon: 'warning',
          title: 'Saknas företagsuppgifter',
          html: `<p>${errorMessage}</p><p style="margin-top: 10px;">Gå till <strong>Inställningar → Företagsinformation</strong> för att lägga till de uppgifter som saknas.</p>`,
          confirmButtonText: 'OK'
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setOperationInProgress(false);
    }
  };

  return (
    <>
      <div className="step-header">
        <div className="step-title">
          <FileText size={24} color="var(--bs-primary)" />
          <h3>AGI-deklaration till Skatteverket</h3>
        </div>
        <p className="step-description">
          Skapa och skicka AGI-fil (Arbetsgivardeklaration) till{' '}
          <a href="https://www.skatteverket.se" target="_blank" rel="noopener noreferrer">Skatteverket</a>{' '}
          senast den 12:e i månaden efter löneperioden.
        </p>
      </div>

      <div className="step-body">
        {/* Show submission status if already submitted */}
        {currentPayroll?.agiSubmitted && (
          <div className="status-badge status-success">
            <CheckCircle2 size={16} />
            AGI-fil skickad till Skatteverket {moment(currentPayroll.agiSubmittedAt).format('YYYY-MM-DD')}
          </div>
        )}

        <div className="action-card">
          <div className="action-card-header">
            <div className="action-card-icon">
              <Download size={20} />
            </div>
            <h4 className="action-card-title">Ladda ner AGI-fil</h4>
          </div>
          <p className="action-card-description">
            Skapa en AGI-fil (XML-format) som innehåller all nödvändig information
            för arbetsgivardeklarationen till Skatteverket.
            {payslips?.length === 0 && ' Filen kommer att innehålla nollbelopp eftersom inga löner betalades ut denna period.'}
          </p>
          <button
            className="action-button btn-primary-action"
            onClick={getAGIXML}
            disabled={(!currentPayroll?.verificationCreated && payslips?.length > 0) || operationInProgress || !isViewingCurrentProgress}
          >
            {operationInProgress ? (
              <div className="loading-spinner" />
            ) : (
              <Download size={16} />
            )}
            Skapa AGI-fil
          </button>
        </div>

        {/* In-step action/undo button for AGI submission */}
        <div className="action-card" style={{ marginTop: '1rem' }}>
          <div className="action-card-header">
            <div className="action-card-icon">
              {currentPayroll?.agiSubmitted ? <CheckCircle2 size={20} /> : <Send size={20} />}
            </div>
            <h4 className="action-card-title">
              {currentPayroll?.agiSubmitted ? 'AGI-deklaration genomförd' : 'Markera AGI som skickad'}
            </h4>
          </div>
          <p className="action-card-description">
            {currentPayroll?.agiSubmitted
              ? 'AGI-deklarationen har skickats till Skatteverket. Om du behöver göra ändringar kan du ångra deklarationen här.'
              : 'Efter att du skickat AGI-filen via Skatteverkets webbtjänst, markera den som skickad här för att fortsätta.'}
          </p>
          <div>
            <button
              className={`action-button ${currentPayroll?.agiSubmitted ? 'btn-danger-outline' : 'btn-success-action'}`}
              onClick={currentPayroll?.agiSubmitted
                ? async () => {
                  // Undo AGI submission
                  const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Ångra AGI-deklaration?',
                    text: 'Detta kommer att återställa AGI-statusen. Du kan då göra ändringar och skicka AGI på nytt.',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, ångra',
                    cancelButtonText: 'Avbryt',
                    confirmButtonColor: '#dc2626'
                  });

                  if (result.isConfirmed) {
                    setOperationInProgress(true);
                    try {
                      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/undo-agi`);
                      const response = await axios.post(url, {}, { withCredentials: true });

                      if (response.data.success) {
                        await onRefreshData();
                        toast.success('AGI-deklaration ångrad');
                      }
                    } catch (err) {
                      console.error('Error undoing AGI:', err);
                      toast.error(err.response?.data?.error || 'Det gick inte att ångra AGI-deklarationen');
                    } finally {
                      setOperationInProgress(false);
                    }
                  }
                }
                : async () => {
                  // Mark as submitted
                  const today = moment().format('YYYY-MM-DD');
                  const result = await Swal.fire({
                    icon: 'question',
                    title: 'Markera AGI som skickad',
                    html:
                      '<p>Bekräfta att du har skickat AGI-filen till Skatteverket.</p>' +
                      '<input id="submission-date" type="date" class="swal2-input" placeholder="Datum skickad" value="' +
                      today + '">' +
                      '<textarea id="notes" class="swal2-textarea" placeholder="Anteckningar (valfritt)"></textarea>',
                    showCancelButton: true,
                    confirmButtonText: 'Markera som skickad',
                    cancelButtonText: 'Avbryt',
                    preConfirm: () => {
                      const date = document.getElementById('submission-date').value;
                      const notes = document.getElementById('notes').value;

                      if (!date) {
                        Swal.showValidationMessage('Datum krävs');
                        return false;
                      }

                      return { submissionDate: date, notes };
                    }
                  });

                  if (result.isConfirmed) {
                    setOperationInProgress(true);
                    try {
                      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/agi/mark-submitted`);
                      const response = await axios.post(url, result.value, { withCredentials: true });

                      if (response.data.success) {
                        await onRefreshData();
                        toast.success('AGI markerad som skickad');
                      }
                    } catch (err) {
                      console.error('Error marking AGI as submitted:', err);
                      toast.error(err.response?.data?.error || 'Det gick inte att markera AGI som skickad');
                    } finally {
                      setOperationInProgress(false);
                    }
                  }
                }
              }
              disabled={operationInProgress || (!currentPayroll?.verificationCreated && !currentPayroll?.agiSubmitted) || !isViewingCurrentProgress}
            >
              {operationInProgress ? (
                <div className="loading-spinner" />
              ) : currentPayroll?.agiSubmitted ? (
                <>
                  <XCircle size={16} />
                  Ångra AGI-deklaration
                </>
              ) : (
                <>
                  <Send size={16} />
                  Markera som skickad
                </>
              )}
            </button>

            {/* Disabled message when taxes paid */}
            {currentPayroll?.agiSubmitted && currentPayroll?.taxesPaid && (
              <small className="text-muted mt-2 d-block" style={{ fontSize: '0.75rem' }}>
                <AlertCircle size={12} style={{ marginBottom: '2px' }} /> Kan inte ångras - skatter är betalda. Du måste ångra skattebetalningen först.
              </small>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

const TaxPaymentStep = ({ currentPayroll, onRefreshData, operationInProgress, setOperationInProgress, isViewingCurrentProgress }) => {
  const { companyId } = useParams();
  const totalTaxes = (parseFloat(currentPayroll?.totalTaxAmount || 0) + parseFloat(currentPayroll?.socialSecurityPaid || 0));

  return (
    <>
      <div className="step-header">
        <div className="step-title">
          <Calculator size={24} color="var(--bs-primary)" />
          <h3>Betala skatter till Skatteverket</h3>
        </div>
        <p className="step-description">
          Betala in avdragen skatt och arbetsgivaravgifter till Skatteverket.
          Detta måste göras senast den 12:e i månaden efter löneperioden.
        </p>
      </div>

      <div className="step-body">
        {/* Show completion status */}
        {currentPayroll?.taxesPaid && (
          <div className="status-badge status-success">
            <CheckCircle2 size={16} />
            Skatter betalda {moment(currentPayroll.taxesPaidAt).format('YYYY-MM-DD')}
            {currentPayroll.taxPaymentReference && ` - Ref: ${currentPayroll.taxPaymentReference}`}
          </div>
        )}

        {/* Payment Summary */}
        <div style={{ background: 'var(--step-warning-bg)', border: '1px solid var(--step-warning-border)', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
          <h5 style={{ color: 'var(--step-warning-text-dark)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Calculator size={20} />
            Totalt att betala till Skatteverket
          </h5>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--step-warning-text-dark)', marginBottom: '1rem' }}>
            {formatCurrency(totalTaxes)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--step-warning-text)' }}>Avdragen skatt</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--step-warning-text-dark)' }}>
                {formatCurrency(currentPayroll?.totalTaxAmount)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--step-warning-text)' }}>Arbetsgivaravgifter</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--step-warning-text-dark)' }}>
                {formatCurrency(currentPayroll?.socialSecurityPaid)}
              </div>
            </div>
          </div>
        </div>

        {/* In-step action/undo button for tax payment */}
        <div className="action-card">
          <div className="action-card-header">
            <div className="action-card-icon">
              {currentPayroll?.taxesPaid ? <CheckCircle2 size={20} /> : <CreditCard size={20} />}
            </div>
            <h4 className="action-card-title">
              {currentPayroll?.taxesPaid ? 'Skatter betalda' : 'Markera skatter som betalda'}
            </h4>
          </div>
          <p className="action-card-description">
            {currentPayroll?.taxesPaid
              ? 'Skatterna har betalats till Skatteverket. Om du behöver göra ändringar kan du ångra betalningen här.'
              : 'Efter att du betalat skatterna via din internetbank eller Skatteverkets webbtjänst, markera dem som betalda här för att fortsätta.'}
          </p>
          <div>
            <button
              className={`action-button ${currentPayroll?.taxesPaid ? 'btn-danger-outline' : 'btn-success-action'}`}
              onClick={currentPayroll?.taxesPaid
                ? async () => {
                  // Undo tax payment
                  const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Ångra skattebetalning?',
                    text: 'Detta kommer att återställa skattebetalningsstatusen. Du kan då göra ändringar och markera som betald på nytt.',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, ångra',
                    cancelButtonText: 'Avbryt',
                    confirmButtonColor: '#dc2626'
                  });

                  if (result.isConfirmed) {
                    setOperationInProgress(true);
                    try {
                      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/undo-taxes`);
                      const response = await axios.post(url, { reason: 'Användaren ångrade skattebetalningen' }, { withCredentials: true });

                      if (response.data.success) {
                        await onRefreshData();
                        toast.success('Skattebetalning ångrad');
                      }
                    } catch (err) {
                      console.error('Error undoing tax payment:', err);
                      toast.error(err.response?.data?.error || 'Det gick inte att ångra skattebetalningen');
                    } finally {
                      setOperationInProgress(false);
                    }
                  }
                }
                : async () => {
                  // Mark as paid
                  const today = moment().format('YYYY-MM-DD');
                  const result = await Swal.fire({
                    icon: 'question',
                    title: 'Markera skatter som betalda',
                    html:
                      '<p>Bekräfta att du har betalat skatterna till Skatteverket.</p>' +
                      '<input id="payment-date" type="date" class="swal2-input" placeholder="Betalningsdatum" value="' +
                      today + '">' +
                      '<input id="payment-reference" type="text" class="swal2-input" placeholder="Betalningsreferens (valfritt)">' +
                      '<textarea id="notes" class="swal2-textarea" placeholder="Anteckningar (valfritt)"></textarea>',
                    showCancelButton: true,
                    confirmButtonText: 'Markera som betalda',
                    cancelButtonText: 'Avbryt',
                    preConfirm: () => {
                      const date = document.getElementById('payment-date').value;
                      const reference = document.getElementById('payment-reference').value;
                      const notes = document.getElementById('notes').value;

                      if (!date) {
                        Swal.showValidationMessage('Betalningsdatum krävs');
                        return false;
                      }

                      return { paymentDate: date, paymentReference: reference, notes };
                    }
                  });

                  if (result.isConfirmed) {
                    setOperationInProgress(true);
                    try {
                      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/taxes/mark-paid`);
                      const response = await axios.post(url, result.value, { withCredentials: true });

                      if (response.data.success) {
                        await onRefreshData();
                        toast.success('Skatter markerade som betalda');
                      }
                    } catch (err) {
                      console.error('Error marking taxes as paid:', err);
                      toast.error(err.response?.data?.error || 'Det gick inte att markera skatter som betalda');
                    } finally {
                      setOperationInProgress(false);
                    }
                  }
                }
              }
              disabled={operationInProgress || (!currentPayroll?.agiSubmitted && !currentPayroll?.taxesPaid) || !isViewingCurrentProgress}
            >
              {operationInProgress ? (
                <div className="loading-spinner" />
              ) : currentPayroll?.taxesPaid ? (
                <>
                  <XCircle size={16} />
                  Ångra skattebetalning
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Markera som betalda
                </>
              )}
            </button>

            {/* Disabled message when withdrawal booked */}
            {currentPayroll?.taxesPaid && currentPayroll?.withdrawalBooked && (
              <small className="text-muted mt-2 d-block" style={{ fontSize: '0.75rem' }}>
                <AlertCircle size={12} style={{ marginBottom: '2px' }} /> Kan inte ångras - uttag är bokfört. Du måste ångra bokföringen av uttag först.
              </small>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const BookWithdrawalStep = ({ currentPayroll, payslips, companyId, onRefreshData, operationInProgress, setOperationInProgress, onViewVerification, isViewingCurrentProgress }) => {
  const totalWithdrawal = (parseFloat(currentPayroll?.totalTaxAmount || 0) + parseFloat(currentPayroll?.socialSecurityPaid || 0));
  const withdrawalDate = moment().year(currentPayroll?.year).month(currentPayroll?.month).date(12).format('YYYY-MM-DD');
  const isWithdrawalDateInFuture = moment(withdrawalDate).isAfter(moment(), 'day');

  const handleCreateVerification = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Skapa bokföringsverifikat',
      html:
        '<p>Skapa verifikat för Skatteverkets uttag den <strong>' + withdrawalDate + '</strong>?</p>' +
        '<textarea id="notes" class="swal2-textarea" placeholder="Anteckningar (valfritt)"></textarea>',
      showCancelButton: true,
      confirmButtonText: 'Skapa verifikat',
      cancelButtonText: 'Avbryt',
      preConfirm: () => {
        const notes = document.getElementById('notes').value;
        return { withdrawalDate, notes };
      }
    });

    if (!formValues) return;

    setOperationInProgress(true);
    try {
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/withdrawal/book`);
      const response = await axios.post(url, formValues, { withCredentials: true });

      if (response.status === 200) {
        await onRefreshData();
        toast.success('Bokföringsverifikatet har skapats för Skatteverkets uttag');
      }
    } catch (err) {
      console.error('Error creating withdrawal verification:', err);
      toast.error(err.response?.data?.message || 'Det gick inte att skapa verifikatet');
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleMarkAsBooked = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Markera som redan bokfört',
      html:
        '<p>Har du redan bokfört Skatteverkets uttag manuellt?</p>' +
        '<input id="withdrawal-date" type="date" class="swal2-input" placeholder="Uttagsdatum" value="' +
        withdrawalDate + '">' +
        '<textarea id="notes" class="swal2-textarea" placeholder="Anteckningar (valfritt)"></textarea>',
      showCancelButton: true,
      confirmButtonText: 'Ja, redan bokfört',
      cancelButtonText: 'Avbryt',
      preConfirm: () => {
        const date = document.getElementById('withdrawal-date').value;
        const notes = document.getElementById('notes').value;

        if (!date) {
          Swal.showValidationMessage('Uttagsdatum krävs');
          return false;
        }

        return { withdrawalDate: date, notes: notes || 'Bokfört manuellt' };
      }
    });

    if (!formValues) return;

    setOperationInProgress(true);
    try {
      const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/withdrawal/mark-booked`);
      const response = await axios.post(url, formValues, { withCredentials: true });

      if (response.status === 200) {
        await onRefreshData();
        toast.success('Uttaget har markerats som bokfört');
      }
    } catch (err) {
      console.error('Error marking withdrawal as booked:', err);
      toast.error(err.response?.data?.message || 'Det gick inte att markera uttaget som bokfört');
    } finally {
      setOperationInProgress(false);
    }
  };

  return (
    <>
      <div className="step-header">
        <div className="step-title">
          <Building2 size={24} color="var(--bs-primary)" />
          <h3>Bokför Skatteverkets uttag</h3>
        </div>
        <p className="step-description">
          Skatteverket gör automatiskt uttag från företagets skattekonto den 12:e varje månad.
          Detta uttag måste bokföras för att hålla bokföringen uppdaterad.
        </p>
      </div>

      <div className="step-body">
        {/* Zero payslips info banner */}
        {payslips?.length === 0 && (
          <div className="info-card" style={{ marginBottom: '1.5rem' }}>
            <Info size={20} className="info-icon" style={{ color: 'var(--bs-primary)' }} />
            <div>
              <div className="info-text">
                <strong>Inget uttag att bokföra</strong><br />
                Eftersom inga löner betalades ut denna period (0 kr i skatter och avgifter) kommer Skatteverket inte göra något uttag.
                Du kan fortsätta direkt till översikten genom att klicka på "Fortsätt".
              </div>
            </div>
          </div>
        )}

        {/* Show completion status */}
        {currentPayroll?.withdrawalBooked && (
          <div className="status-badge status-success">
            <CheckCircle2 size={16} />
            Uttag bokfört {moment(currentPayroll.withdrawalBookedAt).format('YYYY-MM-DD')}
            {currentPayroll.withdrawalManual && ' (Manuellt)'}
          </div>
        )}

        {/* Show withdrawal verification links */}
        {currentPayroll?.withdrawalBooked && !currentPayroll?.withdrawalManual && (() => {
          const withdrawalVerifications = (currentPayroll?.verifications || []).filter(v =>
            v.status === 'ACTIVE' && (v.reference_type === 'skatteverket_tax_withdrawal' || v.reference_type === 'skatteverket_employer_contribution_withdrawal')
          );
          if (withdrawalVerifications.length === 0) return null;
          return (
            <div className="verification-status-inline" style={{ marginTop: '0.5rem' }}>
              <CheckCircle2 size={16} className="text-success" />
              <span>
                Verifikat{' '}
                {withdrawalVerifications.map((v, idx) => (
                  <span key={v.verification_id}>
                    {idx > 0 && ', '}
                    <span
                      className="verification-link"
                      onClick={() => {
                        const verificationId = v.id || v.verification_id || v.verificationId;
                        onViewVerification(verificationId);
                      }}
                      title="Klicka för att visa verifikatdetaljer"
                    >
                      V{v.number}{(v.description || v.verification_description) ? ` (${v.description || v.verification_description})` : ''}
                    </span>
                  </span>
                ))}
                {' '}skapade
              </span>
            </div>
          );
        })()}

        {/* Only show withdrawal actions if there are payslips (something to withdraw) */}
        {payslips?.length > 0 && (
          <>
            {/* Withdrawal Summary */}
            <div style={{ background: 'var(--step-success-bg)', border: '1px solid var(--bs-primary)', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
              <h5 style={{ color: 'var(--step-success-text-dark)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Building2 size={20} />
                Skatteverkets uttag
              </h5>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--step-success-text-dark)', marginBottom: '1rem' }}>
                {formatCurrency(totalWithdrawal)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--step-success-text)' }}>Avdragen skatt</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--step-success-text-dark)' }}>
                    {formatCurrency(currentPayroll?.totalTaxAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--step-success-text)' }}>Arbetsgivaravgifter</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--step-success-text-dark)' }}>
                    {formatCurrency(currentPayroll?.socialSecurityPaid)}
                  </div>
                </div>
              </div>
            </div>

            {/* Action card */}
            <div className="action-card">
              <div className="action-card-header">
                <div className="action-card-icon">
                  {currentPayroll?.withdrawalBooked ? <CheckCircle2 size={20} /> : <FileText size={20} />}
                </div>
                <h4 className="action-card-title">
                  {currentPayroll?.withdrawalBooked ? 'Uttag bokfört' : 'Bokför uttaget'}
                </h4>
              </div>
              <p className="action-card-description">
                {currentPayroll?.withdrawalBooked
                  ? 'Skatteverkets uttag är bokfört.'
                  : 'Skapa ett bokföringsverifikat för uttaget, eller markera som redan bokfört.'}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {!currentPayroll?.withdrawalBooked ? (
                  <>
                    <button
                      className="action-button btn-primary-action"
                      onClick={handleCreateVerification}
                      disabled={operationInProgress || !isViewingCurrentProgress || isWithdrawalDateInFuture}
                    >
                      {operationInProgress ? <div className="loading-spinner" /> : <FileText size={16} />}
                      Skapa verifikat
                    </button>
                    <button
                      className="action-button btn-secondary-action"
                      onClick={handleMarkAsBooked}
                      disabled={operationInProgress || !isViewingCurrentProgress}
                    >
                      <CheckCircle2 size={16} />
                      Redan bokfört
                    </button>
                  </>
                ) : (
                  <button
                    className="action-button btn-danger-outline"
                    onClick={async () => {
                      const result = await Swal.fire({
                        icon: 'warning',
                        title: 'Ångra bokföring av uttag?',
                        text: currentPayroll?.withdrawalManual
                          ? 'Detta kommer att återställa bokföringsstatusen för uttaget.'
                          : 'Detta kommer att ångra både bokföringen och det automatiskt skapade verifikatet.',
                        showCancelButton: true,
                        confirmButtonText: 'Ja, ångra',
                        cancelButtonText: 'Avbryt',
                        confirmButtonColor: '#dc2626'
                      });

                      if (result.isConfirmed) {
                        setOperationInProgress(true);
                        try {
                          const url = getApiUrl(`${companyId}/payroll/${currentPayroll.id}/undo-withdrawal`);
                          const response = await axios.post(url, { reason: 'Användaren ångrade bokföringen av uttag' }, { withCredentials: true });

                          if (response.data.success) {
                            await onRefreshData();
                            toast.success('Bokföring av uttag ångrad');
                          }
                        } catch (err) {
                          console.error('Error undoing withdrawal booking:', err);
                          toast.error(err.response?.data?.error || 'Det gick inte att ångra bokföringen av uttag');
                        } finally {
                          setOperationInProgress(false);
                        }
                      }
                    }}
                    disabled={operationInProgress || !isViewingCurrentProgress}
                  >
                    {operationInProgress ? (
                      <div className="loading-spinner" />
                    ) : (
                      <>
                        <XCircle size={16} />
                        Ångra bokföring
                      </>
                    )}
                  </button>
                )}
              </div>
              {isWithdrawalDateInFuture && !currentPayroll?.withdrawalBooked && (
                <small style={{ fontSize: '0.75rem', color: 'var(--step-warning-text)', display: 'block', marginTop: '0.5rem' }}>
                  <AlertCircle size={12} style={{ marginBottom: '2px' }} /> Uttagsdatum {withdrawalDate} har inte passerat ännu
                </small>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

const OverviewStep = ({ currentPayroll, payslips }) => {
  const totalGross = payslips?.reduce((sum, p) => sum + parseFloat(p.grosspay || 0), 0) || 0;
  const totalNet = payslips?.reduce((sum, p) => sum + parseFloat(p.netpay || 0), 0) || 0;
  const totalTax = payslips?.reduce((sum, p) => sum + parseFloat(p.tax || 0), 0) || 0;
  const totalSocial = payslips?.reduce((sum, p) => sum + parseFloat(p.socialseccontr || 0), 0) || 0;
  const totalCost = totalGross + totalSocial;

  return (
    <>
      <div className="step-header">
        <div className="step-title">
          <TrendingUp size={24} color="var(--bs-success)" />
          <h3>Löneperiod slutförd - Översikt</h3>
        </div>
        <p className="step-description">
          Grattis! Du har slutfört alla steg för denna löneperiod.
          Här är en sammanfattning av lönekörningen.
        </p>
      </div>

      <div className="step-body">
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: 'linear-gradient(135deg, var(--step-success-bg-light) 0%, var(--step-success-border) 100%)', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Users size={24} color="var(--step-success-text)" />
              <h6 style={{ margin: 0, color: 'var(--step-success-text)' }}>Anställda</h6>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--step-success-text-dark)' }}>
              {payslips?.length || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--step-success-text-medium)' }}>lönespecifikationer</div>
          </div>

          <div style={{ background: 'linear-gradient(135deg, var(--step-success-bg-light) 0%, var(--step-success-border) 100%)', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <TrendingUp size={24} color="var(--step-success-text)" />
              <h6 style={{ margin: 0, color: 'var(--step-success-text)' }}>Total kostnad</h6>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--step-success-text-dark)' }}>
              {formatCurrency(totalCost)}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--step-success-text-medium)' }}>brutto + avgifter</div>
          </div>

          <div style={{ background: 'linear-gradient(135deg, var(--step-warning-bg) 0%, var(--step-warning-border) 100%)', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Calculator size={24} color="var(--step-warning-text-dark)" />
              <h6 style={{ margin: 0, color: 'var(--step-warning-text-dark)' }}>Skatter & avgifter</h6>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--step-warning-text-dark)' }}>
              {formatCurrency(totalTax + totalSocial)}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--step-warning-text)' }}>till Skatteverket</div>
          </div>

          <div style={{ background: 'linear-gradient(135deg, var(--step-success-bg) 0%, var(--step-success-bg-light) 100%)', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <CreditCard size={24} color="var(--step-success-text)" />
              <h6 style={{ margin: 0, color: 'var(--step-success-text)' }}>Nettoutbetalning</h6>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--step-success-text-dark)' }}>
              {formatCurrency(totalNet)}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--step-success-text-medium)' }}>till anställda</div>
          </div>
        </div>

      </div>
    </>
  );
};


// Add this PayslipsTable component after the step components
const PayslipsTable = ({ payslips, companyId, onDeletePayslip, isLocked }) => {
  const history = useHistory();

  const PAYSLIPS_COLUMNS = useMemo(() => [
    {
      Header: 'Anställd',
      accessor: 'name',
      Cell: ({ value }) => <span className="fw-medium">{value}</span>
    },
    {
      Header: 'Bruttolön',
      accessor: 'grosspay',
      Cell: ({ value }) => <span>{formatCurrency(value)}</span>
    },
    {
      Header: 'Skatt',
      accessor: 'tax',
      Cell: ({ value }) => <span>{formatCurrency(value)}</span>
    },
    {
      Header: 'Netto',
      accessor: 'netpay',
      Cell: ({ value }) => <span>{formatCurrency(value)}</span>
    },
    {
      Header: 'Soc. Avg.',
      accessor: 'socialseccontr',
      Cell: ({ value }) => <span>{formatCurrency(value)}</span>
    },
    {
      Header: 'Tot. Lönekostnad',
      accessor: 'totalEmployeeCost',
      Cell: ({ value }) => (
        <span className="fw-medium">{formatCurrency(value)}</span>
      )
    },
    {
      Header: "",
      accessor: 'actions',
      Cell: ({ row }) => (
        <Dropdown
          className="ms-auto text-right"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown.Toggle variant="" className="btn-link i-false border-0 p-0">
            <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
              <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                <rect x={0} y={0} width={24} height={24} />
                <circle fill="#000000" cx={5} cy={12} r={2} />
                <circle fill="#000000" cx={12} cy={12} r={2} />
                <circle fill="#000000" cx={19} cy={12} r={2} />
              </g>
            </svg>
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu dropdown-menu-right">
            <Dropdown.Item
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row.original.id);
              }}
              disabled={isLocked}
            >
              {isLocked ? 'Visa lönebesked' : 'Ändra lönebesked'}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id);
              }}
              disabled={isLocked}
            >
              Radera lönebesked
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
      id: 'actions'
    }
  ], [isLocked]);

  // row click
  const handleRowClick = (payslipId) => {
    history.push(`/${companyId}/payslip/${payslipId}`);
  };

  // delete row
  const handleDelete = async (payslipId) => {
    const confirm = await Swal.fire({
      title: 'Är du säker?',
      text: "Denna åtgärd kan inte ångras!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, radera',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545',
      reverseButtons: true
    });

    if (!confirm.isConfirmed) return;

    // Call the parent handler to perform the deletion
    if (onDeletePayslip) {
      onDeletePayslip(payslipId);
    }
  };

  // React Table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page
  } = useTable(
    {
      columns: PAYSLIPS_COLUMNS,
      data: payslips || [],
      initialState: { pageIndex: 0 }
    },
    useFilters,
    usePagination
  );

  // Calculate totals for the TFOOT row
  const totalGross = payslips ? payslips.reduce((acc, p) => acc + parseFloat(p.grosspay || 0), 0) : 0;
  const totalTax = payslips ? payslips.reduce((acc, p) => acc + parseFloat(p.tax || 0), 0) : 0;
  const totalNet = payslips ? payslips.reduce((acc, p) => acc + parseFloat(p.netpay || 0), 0) : 0;
  const totalSoc = payslips ? payslips.reduce((acc, p) => acc + parseFloat(p.socialseccontr || 0), 0) : 0;
  const totalCost = payslips ? payslips.reduce((acc, p) => acc + parseFloat(p.totalEmployeeCost || 0), 0) : 0;

  if (!payslips || payslips.length === 0) {
    return (
      <Alert variant="info">
        Inga lönebesked finns för denna period
      </Alert>
    );
  }

  return (
    <div className="table-responsive">
      <table {...getTableProps()} className="table dataTable table-hover">
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={`header-${index}`}>
              {headerGroup.headers.map((col) => (
                <th {...col.getHeaderProps()} key={col.id}>
                  {col.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            const rowKey = row.original.id || `payslip-${index}`;

            return (
              <tr
                key={rowKey}
                {...row.getRowProps()}
                className="border-bottom clickable-row"
                style={{ cursor: 'pointer' }}
                onClick={() => handleRowClick(row.original.id)}
              >
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} key={cell.column.id}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>

        {/* TFOOT - totals row */}
        <tfoot>
          <tr key="totals-row" className="table-light">
            <td><strong>Totaler:</strong></td>
            {/* gross */}
            <td><strong>{formatCurrency(totalGross)}</strong></td>
            {/* tax */}
            <td><strong>{formatCurrency(totalTax)}</strong></td>
            {/* net */}
            <td><strong>{formatCurrency(totalNet)}</strong></td>
            {/* soc */}
            <td><strong>{formatCurrency(totalSoc)}</strong></td>
            {/* total cost */}
            <td><strong>{formatCurrency(totalCost)}</strong></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};


// Add this component after PayrollStepContent
const StepNavigationBar = ({
  currentPayroll,
  payslips,
  activeStep,
  onStepChange,
  canProceedToNext,
  canGoBack,
  isBackwardBlocked,
  getBackwardBlockMessage,
  PAYROLL_STEPS,
  navigateToStep,
  setActiveStep,
  companyId,
  onRefreshData
}) => {
  // Local state for operations within navigation (e.g., marking AGI)
  const [operationInProgress, setOperationInProgress] = useState(false);
  const steps = [
    { key: PAYROLL_STEPS.MANAGE_PAYSLIPS.id, number: 1, title: 'Lönespecifikationer' },
    { key: PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id, number: 2, title: 'Betala & Bokför' },
    { key: PAYROLL_STEPS.AGI_DECLARATION.id, number: 3, title: 'AGI Deklaration' },
    { key: PAYROLL_STEPS.PAY_TAXES.id, number: 4, title: 'Betala Skatter' },
    { key: PAYROLL_STEPS.BOOK_WITHDRAWAL.id, number: 5, title: 'Bokför Uttag' },
    { key: PAYROLL_STEPS.OVERVIEW.id, number: 6, title: 'Översikt' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === activeStep);
  const currentStepNumber = currentStepIndex + 1;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Determine if user is viewing their current progress step
  // If currentStep is not set (new payroll), default to first step
  const currentProgressStep = currentPayroll?.currentStep || PAYROLL_STEPS.MANAGE_PAYSLIPS.id;
  const isViewingCurrentProgress = activeStep === currentProgressStep;

  // Get the progress step info for messaging
  const progressStepIndex = steps.findIndex(step => step.key === currentProgressStep);
  const progressStepNumber = progressStepIndex + 1;

  const handlePrevious = async () => {
    if (isFirstStep || operationInProgress || isBackwardBlocked()) return;

    const previousStep = steps[currentStepIndex - 1];
    await onStepChange(previousStep.key, 'backward');
  };

  const handleNext = async () => {
    if (isLastStep || operationInProgress) return;

    // Check general requirements
    // All steps now use in-step buttons for actions
    // Forward button only navigates
    if (!canProceedToNext) {
      toast.warning(getNextStepRequirements() || 'Du måste slutföra detta steg först');
      return;
    }

    const nextStep = steps[currentStepIndex + 1];
    await onStepChange(nextStep.key, 'forward');
  };

  // Get contextual button labels based on step
  const getForwardButtonLabel = () => {
    // Simple labels - button only navigates, never performs actions
    const nextStep = steps[currentStepIndex + 1];
    return nextStep ? `Fortsätt` : 'Fortsätt';
  };

  const getBackwardButtonLabel = () => {
    // Simple backward navigation - no undo labels
    // Undo is always done via in-step undo buttons
    return 'Tillbaka';
  };

  const getNextStepRequirements = () => {
    switch (activeStep) {
      case PAYROLL_STEPS.MANAGE_PAYSLIPS.id:
        return !currentPayroll?.isLocked ? 'Du måste låsa perioden för att fortsätta' : null;
      case PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id:
        // Allow progression without verification if there are no payslips (nothing to book)
        if (payslips?.length === 0) {
          return null; // No requirement - can proceed without verification
        }
        return !currentPayroll?.verificationCreated ? 'Du måste skapa bokföringsverifikat för att fortsätta' : null;
      case PAYROLL_STEPS.AGI_DECLARATION.id:
        // No requirement message - forward button handles the marking
        return null;
      case PAYROLL_STEPS.PAY_TAXES.id:
        // No requirement message - forward button handles the marking
        return null;
      default:
        return null;
    }
  };

  const requirementMessage = getNextStepRequirements();

  return (
    <>
      <style>{`
        .step-navigation-bar {
          background: white;
          border: 1px solid var(--step-border-light);
          border-radius: 8px;
          padding: 1.5rem 2rem;
          margin-top: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .step-nav-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .step-nav-current {
          font-weight: 600;
          color: var(--step-text-heading);
          font-size: 1rem;
        }

        .step-nav-requirement {
          color: var(--step-danger-text);
          font-size: 0.875rem;
          font-style: italic;
        }

        .step-nav-viewing-message {
          color: var(--bs-primary);
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--step-success-bg);
          border-radius: 6px;
          margin-top: 0.5rem;
        }

        .step-nav-viewing-message button {
          background: none;
          border: none;
          color: var(--primary-hover);
          text-decoration: underline;
          cursor: pointer;
          font-weight: 600;
          padding: 0;
          margin: 0 0.25rem;
        }

        .step-nav-viewing-message button:hover {
          color: var(--primary);
        }

        .step-nav-completion-message {
          color: var(--step-success-accent);
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: var(--step-success-border-light);
          border-radius: 6px;
          margin-top: 0.5rem;
          font-weight: 500;
        }

        .step-nav-block-message {
          color: var(--step-danger-text);
          font-size: 0.75rem;
          font-style: italic;
          margin-top: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--step-danger-bg);
          border: 1px solid var(--step-danger-border);
          border-radius: 6px;
        }
        
        .step-nav-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        
        .step-nav-btn {
          border-radius: 6px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          min-width: 120px;
          justify-content: center;
        }
        
        .btn-nav-previous {
          background: var(--step-bg);
          color: var(--bs-gray);
          border: 1px solid var(--step-border);
        }

        .btn-nav-previous:hover:not(:disabled) {
          background: var(--step-bg-hover);
          color: var(--step-text-secondary);
        }
        
        .btn-nav-next {
          background: var(--bs-primary);
          color: white;
        }

        .btn-nav-next:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }
        
        .btn-nav-complete {
          background: var(--bs-primary);
          color: white;
        }

        .btn-nav-complete:hover:not(:disabled) {
          background: var(--primary-hover);
        }
        
        .step-nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        
        .step-nav-loading {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .step-navigation-bar {
            flex-direction: column;
            gap: 1rem;
            padding: 1rem;
          }
          
          .step-nav-actions {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>

      <div className="step-navigation-bar">
        <div className="step-nav-info">
          <div className="step-nav-current">
            Steg {currentStepNumber} av {steps.length}: {steps[currentStepIndex]?.title}
          </div>

          {/* Message when viewing a different step */}
          {!isViewingCurrentProgress && progressStepIndex >= 0 && (
            <div className="step-nav-viewing-message">
              <Eye size={16} />
              <span>
                Du tittar på ett tidigare steg.
                <button
                  onClick={() => navigateToStep && navigateToStep(currentProgressStep)}
                  type="button"
                >
                  Gå till Steg {progressStepNumber}
                </button>
                ({steps[progressStepIndex]?.title}) för att fortsätta.
              </span>
            </div>
          )}

          {/* Requirement message when on current step but can't proceed */}
          {isViewingCurrentProgress && requirementMessage && (
            <div className="step-nav-requirement">
              {requirementMessage}
            </div>
          )}

          {/* Completion message on last step */}
          {isViewingCurrentProgress && isLastStep && (
            <div className="step-nav-completion-message">
              <CheckCircle2 size={16} />
              Lönekörningen är slutförd! Du kan nu granska översikten och exportera rapporter.
            </div>
          )}
        </div>

        {/* Only show action buttons when viewing current progress step */}
        {isViewingCurrentProgress && (
          <div className="step-nav-actions">
            {/* Show backward button if not first step */}
            {!isFirstStep && (
              <div>
                <button
                  className="step-nav-btn btn-nav-previous"
                  onClick={handlePrevious}
                  disabled={operationInProgress || isBackwardBlocked()}
                >
                  {operationInProgress ? (
                    <div className="step-nav-loading" />
                  ) : (
                    <ArrowLeft size={16} />
                  )}
                  {getBackwardButtonLabel()}
                </button>
                {/*isBackwardBlocked() && (
                  <div className="step-nav-block-message">
                    {getBackwardBlockMessage()}
                  </div>
                )*/}
              </div>
            )}

            {/* Show forward button if not last step */}
            {!isLastStep && (
              <button
                className="step-nav-btn btn-nav-next"
                onClick={handleNext}
                disabled={operationInProgress || !canProceedToNext}
              >
                {operationInProgress ? (
                  <div className="step-nav-loading" />
                ) : (
                  <ArrowRight size={16} />
                )}
                {getForwardButtonLabel()}
              </button>
            )}

            {/* No "Slutförd" button on last step - removed as requested */}
          </div>
        )}
      </div>
    </>
  );
};